import React, { useState, useRef, useEffect } from "react";
import { updateUser } from "../../api/auth";
import { Snackbar, Alert } from '@mui/material';
import { setUserDetails } from "../../Redux/Slices/userDetailsSlice";
import { useDispatch, useSelector } from "react-redux";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import Loader from "../Loader";
import { getToken } from "../../api/index";
import "./EditLanguage.css";

function EditLanguage() {
    const userDetails = useSelector((state) => state.userDetails.userDetails);
    const dispatch = useDispatch();
    const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
    const [selectedLanguages, setSelectedLanguages] = useState(userDetails?.languages || []);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const languageDropdownRef = useRef(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const allLanguages = [
        { label: 'English', value: 'English' },
        { label: 'Japanese', value: 'Japanese' },
        { label: 'Hindi', value: 'Hindi' },
        { label: 'Spanish', value: 'Spanish' },
        { label: 'French', value: 'French' },
        { label: 'German', value: 'German' },
        { label: 'Chinese', value: 'Chinese' },
        { label: 'Russian', value: 'Russian' },
        { label: 'Arabic', value: 'Arabic' },
        { label: 'Portuguese', value: 'Portuguese' },
        { label: 'Italian', value: 'Italian' },
        { label: 'Korean', value: 'Korean' },
        { label: 'Turkish', value: 'Turkish' },
        { label: 'Dutch', value: 'Dutch' },
        { label: 'Swedish', value: 'Swedish' },
        { label: 'Thai', value: 'Thai' },
        { label: 'Greek', value: 'Greek' },
        { label: 'Vietnamese', value: 'Vietnamese' },
        { label: 'Polish', value: 'Polish' },
        { label: 'Bengali', value: 'Bengali' },
    ];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
                setLanguageMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const handleLanguageOnChange = (language) => {
        if (selectedLanguages.includes(language)) {
            removeLanguage(language);
            return;
        }

        if (selectedLanguages.length >= 5) {
            setError("You can only select up to 5 languages");
            setLanguageMenuOpen(false);
            return;
        }

        setSelectedLanguages([...selectedLanguages, language]);
        setError('');
        setLanguageMenuOpen(false);
    };

    const removeLanguage = (langToRemove) => {
        setSelectedLanguages(
            selectedLanguages.filter((lang) => lang !== langToRemove)
        );
    };

    const handleSubmitClick = async () => {
        if (isLoading) {
            return;
        }
        if (selectedLanguages.length === 0) {
            setError("Please select at least one language");
            return;
        }

        const payload = {
            "languages": selectedLanguages,
            "userId": userDetails.userId
        };
        const token = getToken();
        setIsLoading(true);

        try {
            const response = await updateUser(payload, token);
            console.log("response from updateUser API", response);
            showSnackbar("Languages updated successfully!", "success");
            dispatch(setUserDetails({ ...userDetails, languages: selectedLanguages }));
        } catch (err) {
            console.log("Error updating languages:", err);
            showSnackbar("Failed to update languages. Please try again.", "error");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="EditLanguage">
            <div className="editLanguageContainer">
                <div className="editLanguageCard">
                    <span className="editLanguageLabel">What languages do you speak (up to 5):</span>

                    <div className="languageDropdownContainer" ref={languageDropdownRef}>
                        <div
                            id="language"
                            className="languageSelectInput"
                            onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
                        >
                            {selectedLanguages.length === 0 ? (
                                <span className="languagePlaceholder">Select Languages</span>
                            ) : (
                                <div className="selectedLanguageTags">
                                    {selectedLanguages.map((lang) => (
                                        <span key={lang} className="languageChip">
                                            <span className="languageChipText">{lang}</span>
                                            <span
                                                className="languageChipRemoveBtn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeLanguage(lang);
                                                }}
                                                title={`Remove ${lang}`}
                                            >
                                                <ClearRoundedIcon sx={{ fontSize: 13 }} />
                                            </span>
                                        </span>
                                    ))}
                                </div>
                            )}
                            <span className="languageArrowIcon">
                                {languageMenuOpen ? (
                                    <KeyboardArrowUpRoundedIcon />
                                ) : (
                                    <KeyboardArrowDownRoundedIcon />
                                )}
                            </span>
                        </div>

                        {languageMenuOpen && (
                            <ul className="languageDropdownMenu">
                                {allLanguages.map((lang) => {
                                    const isSelected = selectedLanguages.includes(lang.value);
                                    return (
                                        <li
                                            key={lang.value}
                                            className={`languageDropdownItem ${isSelected ? 'selected' : ''}`}
                                            onClick={() => handleLanguageOnChange(lang.value)}
                                        >
                                            <span>{lang.label}</span>
                                            {isSelected && <span className="languageSelectedCheck">✓</span>}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                    {error && <span className="languageErrorText">{error}</span>}
                </div>

                <div className="editLanguageSubmitBtnContainer">
                    <button
                        className="editLanguageSubmitBtn"
                        onClick={handleSubmitClick}
                        disabled={isLoading}
                    >
                        {isLoading ? <Loader /> : "Update"}
                    </button>
                </div>
            </div>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default EditLanguage;
